/**
 * Helpers for working with RC "center" Contentful metadata tags
 * (e.g. `centerCorporate`, `centerCivil`).
 *
 * These tags drive:
 *  - Site Config `rcTagNames` — the per-site allowlist of which centers a
 *    deployment is allowed to surface (e.g. corporate-prod only renders
 *    `centerCorporate` content).
 *  - Landing page scoping — the `pageHome` entry's own center metadata tag
 *    further narrows which entries its grid should fetch.
 *  - URL routing — see `src/lib/url/blogPrefixMap.ts`.
 */

import {
  CENTER_TAG_TO_SLUG,
  BLOG_SLUG_ALL,
  BLOG_SLUG_TO_CENTER_TAGS,
  validateBlogSlugForEntry,
} from "@/lib/url/blogPrefixMap"
import { isCenterAll } from "@/lib/utils"

const CENTER_TAG_IDS = new Set(Object.keys(CENTER_TAG_TO_SLUG))

/**
 * Return the center metadata tag IDs (e.g. `["centerCorporate"]`) on a
 * Contentful entry. Non-center tags (locale, hideEnglish, canonical*, etc.)
 * are filtered out.
 */
export function getEntryCenterTagIds(entry: any): string[] {
  const tags: { id?: string }[] = entry?.contentfulMetadata?.tags ?? []
  return tags
    .map(t => t?.id)
    .filter((id): id is string => !!id && CENTER_TAG_IDS.has(id))
}

/**
 * Compute the effective center filter for a landing page grid.
 *
 *  - Start with the site-wide `rcTagNames` allowlist.
 *  - If the landing page has any center metadata tags, intersect.
 *  - If the intersection is empty (e.g. the landing page is tagged for a
 *    center the site does not allow) fall back to the allowlist so the
 *    grid is still scoped, never wide-open.
 *  - If the landing page has no center tags at all, just use the allowlist.
 */
export function resolveLandingPageCenters(
  pageEntry: any,
  rcTagNames: string[] | undefined,
): string[] {
  const allowlist = rcTagNames ?? []
  const pageCenters = getEntryCenterTagIds(pageEntry)
  if (pageCenters.length === 0) return allowlist
  const intersect = pageCenters.filter(c => allowlist.includes(c))
  return intersect.length > 0 ? intersect : allowlist
}

/**
 * Map a tab `typeToGather` label (as authored in Contentful) to the
 * canonical Contentful `__typename` for the entry type.
 *
 * Tolerant of:
 *  - Plural vs singular (`Articles` / `Article`)
 *  - Casing (`articles` / `ARTICLES`)
 *  - Surrounding whitespace
 */
const TAB_LABEL_TO_TYPE: Record<string, string> = {
  articles: "Article",
  article: "Article",
  videos: "Video",
  video: "Video",
  "case studies": "CaseStudy",
  "case study": "CaseStudy",
  "external resources": "ExternalResource",
  "external resource": "ExternalResource",
  documents: "Document",
  document: "Document",
  "customer stories": "CustomerStory",
  "customer story": "CustomerStory",
}

export function normalizeTabType(label: string | null | undefined): string | null {
  if (!label) return null
  return TAB_LABEL_TO_TYPE[label.trim().toLowerCase()] ?? null
}

/**
 * Narrow the site-wide `rcTagNames` allowlist to the center(s) for the
 * current URL blog slug (e.g. `trimble` → `centerCorporate`).
 *
 * Returns the full allowlist when the slug is missing, `all`, or the site
 * uses `centerAll`.
 */
export function resolveCenterTagsForBlogSlug(
  blogSlug: string | undefined,
  allowableCenters: string[],
): string[] {
  if (
    !blogSlug ||
    blogSlug === BLOG_SLUG_ALL ||
    isCenterAll(allowableCenters)
  ) {
    return allowableCenters
  }

  const tagsForSlug = BLOG_SLUG_TO_CENTER_TAGS[blogSlug] ?? []
  const intersect = tagsForSlug.filter(tag => allowableCenters.includes(tag))
  return intersect.length > 0 ? intersect : tagsForSlug
}

/**
 * True when an RC entry belongs to the center implied by the URL blog slug.
 * Skipped for missing slugs and the `all` wildcard.
 */
export function entryMatchesBlogSlug(
  entry: any,
  blogSlug: string | undefined,
): boolean {
  if (!blogSlug || blogSlug === BLOG_SLUG_ALL) return true
  return validateBlogSlugForEntry(blogSlug, entry)
}

/**
 * Aggregate the union of canonical entry types declared by every tab in a
 * `SectionResourceGrid` (or compatible MXP `LayoutResourceGrid`) section.
 *
 * Returns `[]` if no tabs / no valid types, in which case callers should
 * treat that as "fetch everything" for back-compat.
 */
export function collectTypeToGather(section: any): string[] {
  const tabs =
    section?.resourceGridTabsCollection?.items ??
    (Array.isArray(section?.tabsList)
      ? section.tabsList.map((label: string) => ({ typeToGather: [label] }))
      : [])
  const set = new Set<string>()
  for (const tab of tabs) {
    for (const label of tab?.typeToGather ?? []) {
      const t = normalizeTabType(label)
      if (t) set.add(t)
    }
  }
  return [...set]
}
